import produce from "immer";

import { DIRECTIONS } from "./intxConstants";
import {
  INTERSECTION_NUMBERS,
  TRAFFIC_VOLUME_KEYS,
  VOLUME_SCENARIOS,
  VOLUME_SCENARIOS_ORDER_WEEKDAY_ONLY,
} from "./storageConstants";

const PERCENT_CONVERSION_MULTIPLIER = 100;

export const getUnexpectedCellValueError = ({ cell, value }) => ({
  severity: "error",
  message: `Expected spreadsheet cell ${cell} to be 0, greater than 0, or empty, but it was "${value}". Edit cell and re-upload the template.`,
});

const EMPTY_SCENARIO = {
  [DIRECTIONS.NORTHBOUND]: {},
  [DIRECTIONS.SOUTHBOUND]: {},
  [DIRECTIONS.EASTBOUND]: {},
  [DIRECTIONS.WESTBOUND]: {},
};

const EMPTY_INTERSECTION = {
  [TRAFFIC_VOLUME_KEYS.OPENING_YEAR]: {},
  [TRAFFIC_VOLUME_KEYS.DESIGN_YEAR]: {},
};

export const getEmptyIntxVolumesObject = (isWeekendAnalysis) => {
  return produce(EMPTY_INTERSECTION, (draft) => {
    draft[TRAFFIC_VOLUME_KEYS.OPENING_YEAR].amPeak = EMPTY_SCENARIO;
    draft[TRAFFIC_VOLUME_KEYS.OPENING_YEAR].pmPeak = EMPTY_SCENARIO;
    draft[TRAFFIC_VOLUME_KEYS.DESIGN_YEAR].amPeak = EMPTY_SCENARIO;
    draft[TRAFFIC_VOLUME_KEYS.DESIGN_YEAR].pmPeak = EMPTY_SCENARIO;

    if (isWeekendAnalysis) {
      draft[TRAFFIC_VOLUME_KEYS.OPENING_YEAR].weekendPeak = EMPTY_SCENARIO;
      draft[TRAFFIC_VOLUME_KEYS.DESIGN_YEAR].weekendPeak = EMPTY_SCENARIO;
    }
  });
};

/**
 * @param {boolean} isOpeningYear
 * @param {boolean} isRampTerminalIntersection
 * @returns string - the spreadsheet cell where the given passengers per vehicle data lives
 */
export const getPassengersPerVehicleCell = ({
  isOpeningYear,
  isRampTerminalIntersection,
}) => {
  if (isRampTerminalIntersection) {
    return isOpeningYear ? "T5" : "T8";
  }
  return isOpeningYear ? "S5" : "S8";
};

/**
 * @param {string} intersectionNumber one of INTERSECTION_NUMBERS
 * @param {boolean} isWeekendAnalysis
 * @returns list of scenario names and spreadsheet rows that are used in the ramp terminal spreadsheet in /public
 */
const getScenarioPropsForRampTerminalIntx = ({
  intersectionNumber,
  isWeekendAnalysis,
}) => {
  const isIntx1 = intersectionNumber === INTERSECTION_NUMBERS.INTERSECTION_1;
  return [
    {
      scenario: VOLUME_SCENARIOS.OPENING_YEAR_AM_PEAK,
      spreadsheetRowNumber: isIntx1 ? 5 : 11,
    },
    {
      scenario: VOLUME_SCENARIOS.OPENING_YEAR_PM_PEAK,
      spreadsheetRowNumber: isIntx1 ? 6 : 12,
    },
    {
      scenario: VOLUME_SCENARIOS.OPENING_YEAR_WEEKEND_PEAK,
      spreadsheetRowNumber: isIntx1 ? 7 : 13,
    },
    {
      scenario: VOLUME_SCENARIOS.DESIGN_YEAR_AM_PEAK,
      spreadsheetRowNumber: isIntx1 ? 8 : 14,
    },
    {
      scenario: VOLUME_SCENARIOS.DESIGN_YEAR_PM_PEAK,
      spreadsheetRowNumber: isIntx1 ? 9 : 15,
    },
    {
      scenario: VOLUME_SCENARIOS.DESIGN_YEAR_WEEKEND_PEAK,
      spreadsheetRowNumber: isIntx1 ? 10 : 16,
    },
  ].filter(
    ({ scenario }) =>
      isWeekendAnalysis ||
      VOLUME_SCENARIOS_ORDER_WEEKDAY_ONLY.includes(scenario)
  );
};

/**
 * @param {boolean} isWeekendAnalysis
 * @returns list of scenario names and spreadsheet rows that are used in the single intx spreadsheet in /public
 * this spreadsheet is used for at grade life cycle cost analyses and capX analyses
 */
const getScenarioPropsForOneIntx = (isWeekendAnalysis) => {
  return [
    {
      scenario: VOLUME_SCENARIOS.OPENING_YEAR_AM_PEAK,
      spreadsheetRowNumber: 5,
    },
    {
      scenario: VOLUME_SCENARIOS.OPENING_YEAR_PM_PEAK,
      spreadsheetRowNumber: 6,
    },
    {
      scenario: VOLUME_SCENARIOS.OPENING_YEAR_WEEKEND_PEAK,
      spreadsheetRowNumber: 7,
    },
    { scenario: VOLUME_SCENARIOS.DESIGN_YEAR_AM_PEAK, spreadsheetRowNumber: 8 },
    { scenario: VOLUME_SCENARIOS.DESIGN_YEAR_PM_PEAK, spreadsheetRowNumber: 9 },
    {
      scenario: VOLUME_SCENARIOS.DESIGN_YEAR_WEEKEND_PEAK,
      spreadsheetRowNumber: 10,
    },
  ].filter(
    ({ scenario }) =>
      isWeekendAnalysis ||
      VOLUME_SCENARIOS_ORDER_WEEKDAY_ONLY.includes(scenario)
  );
};

export const getScenarioSpreadsheetProps = ({
  intersectionNumber,
  isCapXFlow,
  isRampTerminalIntersection,
  isWeekendAnalysis,
}) => {
  if (!isCapXFlow && isRampTerminalIntersection) {
    return getScenarioPropsForRampTerminalIntx({
      intersectionNumber,
      isWeekendAnalysis,
    });
  }
  return getScenarioPropsForOneIntx(isWeekendAnalysis);
};

/**
 * columns where data is stored in single intersection volume template xlsx spreadsheet
 * this spreadsheet is used for cap x flows and the at grade instersection life cycle cost flow
 */
const SINGLE_INTX_XLSX_COLUMNS = [
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
];

/**
 * columns where data is stored in ramp terminal intersection volume template xlsx spreadsheet
 */
const RAMP_TERMINAL_XLSX_COLUMNS =
  SINGLE_INTX_XLSX_COLUMNS.slice(1).concat("S");

/**
 * @returns list that links spreadsheet cells to volume scenario's JS object paths
 */
export const getCellMappingForScenario = ({
  isRampTerminalIntersection,
  scenario,
  spreadsheetRowNumber: n,
}) => {
  const col = isRampTerminalIntersection
    ? RAMP_TERMINAL_XLSX_COLUMNS
    : SINGLE_INTX_XLSX_COLUMNS;
  return [
    {
      cell: `${col[0]}${n}`,
      path: `${scenario}.${DIRECTIONS.NORTHBOUND}.left`,
    },
    {
      cell: `${col[1]}${n}`,
      path: `${scenario}.${DIRECTIONS.NORTHBOUND}.through`,
    },
    {
      cell: `${col[2]}${n}`,
      path: `${scenario}.${DIRECTIONS.NORTHBOUND}.right`,
    },
    {
      cell: `${col[3]}${n}`,
      path: `${scenario}.${DIRECTIONS.SOUTHBOUND}.left`,
    },
    {
      cell: `${col[4]}${n}`,
      path: `${scenario}.${DIRECTIONS.SOUTHBOUND}.through`,
    },
    {
      cell: `${col[5]}${n}`,
      path: `${scenario}.${DIRECTIONS.SOUTHBOUND}.right`,
    },
    { cell: `${col[6]}${n}`, path: `${scenario}.${DIRECTIONS.EASTBOUND}.left` },
    {
      cell: `${col[7]}${n}`,
      path: `${scenario}.${DIRECTIONS.EASTBOUND}.through`,
    },
    {
      cell: `${col[8]}${n}`,
      path: `${scenario}.${DIRECTIONS.EASTBOUND}.right`,
    },
    { cell: `${col[9]}${n}`, path: `${scenario}.${DIRECTIONS.WESTBOUND}.left` },
    {
      cell: `${col[10]}${n}`,
      path: `${scenario}.${DIRECTIONS.WESTBOUND}.through`,
    },
    {
      cell: `${col[11]}${n}`,
      path: `${scenario}.${DIRECTIONS.WESTBOUND}.right`,
    },
    {
      cell: `${col[12]}${n}`,
      multiplier: PERCENT_CONVERSION_MULTIPLIER, // need to multiply by 100 when converting from excel to how our web inputs deal with percentages
      path: `${scenario}.${DIRECTIONS.EASTBOUND}.truckPct`,
    },
    {
      cell: `${col[13]}${n}`,
      multiplier: PERCENT_CONVERSION_MULTIPLIER,
      path: `${scenario}.${DIRECTIONS.WESTBOUND}.truckPct`,
    },
    {
      cell: `${col[14]}${n}`,
      multiplier: PERCENT_CONVERSION_MULTIPLIER,
      path: `${scenario}.${DIRECTIONS.NORTHBOUND}.truckPct`,
    },
    {
      cell: `${col[15]}${n}`,
      multiplier: PERCENT_CONVERSION_MULTIPLIER,
      path: `${scenario}.${DIRECTIONS.SOUTHBOUND}.truckPct`,
    },
  ];
};
